import React, {useCallback, useEffect, useRef, useState} from "react";
import loadScript from "@braintree/asset-loader/dist/load-script";
import {displayName, INTERNAL_ERROR_TYPE, isoLocales, retrieveCheckout, Payments} from "../PaymentController";
import {getShopId} from "../../getShopId";
import braintree, {Client} from "braintree-web";
import {useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import {ButtonPropType} from "../paypal/helpers/paypalTypes";
import {useTranslation} from "react-i18next";
import {Checkout} from "@connox/typescript-library";
import {IPaymentTransaction} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import ApplePayPaymentRequest = ApplePayJS.ApplePayPaymentRequest;
import ApplePayValidateMerchantEvent = ApplePayJS.ApplePayValidateMerchantEvent;
import ApplePayShippingContactSelectedEvent = ApplePayJS.ApplePayShippingContactSelectedEvent;
import ApplePayShippingMethod = ApplePayJS.ApplePayShippingMethod;
import ApplePayShippingMethodSelectedEvent = ApplePayJS.ApplePayShippingMethodSelectedEvent;
import ApplePayPaymentMethodSelectedEvent = ApplePayJS.ApplePayPaymentMethodSelectedEvent;
import ApplePayPaymentAuthorizedEvent = ApplePayJS.ApplePayPaymentAuthorizedEvent;
import ApplePayCouponCodeChangedEvent = ApplePayJS.ApplePayCouponCodeChangedEvent;
import ApplePayErrorContactField = ApplePayJS.ApplePayErrorContactField;
import ApplePayErrorCode = ApplePayJS.ApplePayErrorCode;
import {
  appleAddressAdapter,
  asyncCouponCodeChange,
  asyncPaymentAuthorize,
  asyncPutShippingCountry,
  asyncPutShippingMethod,
  positionsToLineItems,
  putPaymentMethod,
} from "./applePayHelperFunctions";
import {PayPalLoadingSpinner} from "../paypal/BraintreeButton";

export type ErrorMagicAdditionalType =
  | IPaymentTransaction
  | ApplePayCouponCodeChangedEvent
  | ApplePayPaymentAuthorizedEvent
  | ApplePayPaymentMethodSelectedEvent
  | ApplePayShippingContactSelectedEvent
  | ApplePayShippingMethodSelectedEvent
  | ApplePayValidateMerchantEvent;

export type PaymentStateType = {
  paymentRequest: ApplePayPaymentRequest;
  applePayInstance: braintree.ApplePay;
};

type ApplePayButtonLoaderProps = Omit<ButtonPropType, "isExpress"> & {supportedVersion: number};

// region declares to work with `ApplePaySession`

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "apple-pay-button": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          buttonstyle: string;
          type: string;
          locale: string;
        },
        HTMLElement
      >;
    }
  }
  interface Window {
    ApplePaySession: ApplePaySession | undefined;
  }
}

// endregion declares to work with `ApplePaySession`

// Will be deleted on end of debugging and console.log removal!
/* eslint-disable no-console */

const ApplePayButtonLoader: React.FC<ApplePayButtonLoaderProps> = ({
  handleError,
  isBlocked,
  onReturn,
  supportedVersion,
}) => {
  const {braintreeData, cart, countries, setViewState, viewState} = useCartContext();
  const {t} = useTranslation();
  const elemRef = useRef<HTMLElement | null>(null);
  const checkoutRef = useRef<Checkout>();
  const [paymentData, setPaymentData] = useState<PaymentStateType>();
  const paymentDataRef = useRef(paymentData);
  const shippingMethodsRef = useRef<ApplePayShippingMethod[] | undefined>(undefined);
  paymentDataRef.current = paymentData;
  const isBlockedRef = useRef<boolean>(isBlocked);
  isBlockedRef.current = isBlocked;

  // Just a trigger to then assign the onclick handler _natively_ to the `elementRef.current`.
  // See `if` statement right before the `return`
  const [, setScriptLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (!braintreeData) {
      return;
    }

    async function loadData(client: Client) {
      const applePayClient = await braintree.applePay.create({client});

      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest/1916122-supportednetworks
      const supportedNetworks: string[] = ["visa", "masterCard", "amex"];
      if (supportedVersion >= 4) {
        supportedNetworks.push("cartesBancaires");
        supportedNetworks.push("maestro");
        supportedNetworks.push("electron");
        supportedNetworks.push("vPay");
        if (supportedVersion >= 5) {
          supportedNetworks.push("mada");
        }
      }

      const tempPaymentRequest: Omit<ApplePayPaymentRequest, "countryCode" | "merchantCapabilities"> = {
        currencyCode: cart.currencyCode,
        requiredBillingContactFields: ["email", "name", "postalAddress"],
        requiredShippingContactFields: ["email", "name", "postalAddress"],
        shippingContactEditingMode: "available",
        supportedCountries: countries.map((country) => country.countryCode),
        supportsCouponCode: true,
        supportedNetworks,
        total: {
          label: t("cart.total"),
          amount: cart.valuePayable.amount,
        },
        lineItems: positionsToLineItems(cart),
      };

      const paymentRequest = applePayClient.createPaymentRequest(tempPaymentRequest);
      setPaymentData({paymentRequest, applePayInstance: applePayClient});
    }
    void loadData(braintreeData.client);
  }, [braintreeData]);

  /**
   * Mounts the script tag, that loads the Apple Pay js, that
   * captures the already mounted `<apple-pay-button>` tag
   */
  useEffect(() => {
    if (!paymentData) {
      return;
    }
    void loadScript({
      crossorigin: true,
      src: "https://applepay.cdn-apple.com/jsapi/v1.1.0/apple-pay-sdk.js",
    }).then(() => setScriptLoaded(true));
  }, [paymentData]);

  const doErrorMagic = useCallback(
    (
      error: braintree.BraintreeError | Error,
      errorNamePrefix: string,
      additional?: Record<string, ErrorMagicAdditionalType | undefined> | {validationURL: string},
    ) => {
      try {
        error.name = errorNamePrefix + error.name;
      } catch (e) {
        if (__DEVELOPMENT__) {
          // eslint-disable-next-line no-console
          console.log("Non-renamable error:", (e as Error).name);
        }
      }
      handleError(error, "Apple Pay", {...additional, supportedVersion});
    },
    [paymentData],
  );

  if (!paymentData) {
    return (
      <div className="apple-pay-button-container">
        <div className="spinner-container">
          <PayPalLoadingSpinner />
        </div>
      </div>
    );
  }

  const onApplePayButtonClicked = () => {
    if (isBlockedRef.current) {
      return;
    }

    if (!paymentDataRef.current) {
      doErrorMagic(new Error("No 'paymentDataRef.current' at beginning of click handler"), "NoRef");

      return;
    }

    try {
      const session = new ApplePaySession(supportedVersion, paymentDataRef.current.paymentRequest);

      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778021-onvalidatemerchant
      session.onvalidatemerchant = (event: ApplePayValidateMerchantEvent) => {
        console.log("Merchant validation started");
        /* eslint-disable @typescript-eslint/no-unsafe-assignment */
        let validationResult;
        void new Promise<braintree.ApplePay>((resolve, reject) => {
          if (!paymentDataRef.current) {
            reject(new Error("No `paymentDataRef.current` on merchant validation"));
          } else {
            resolve(paymentDataRef.current.applePayInstance);
          }
        })
          .then(async (applePayInstance) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            validationResult = await applePayInstance.performValidation({
              validationURL: event.validationURL,
              displayName,
            });

            checkoutRef.current = await retrieveCheckout(cart, Payments.ApplePay);

            session.completeMerchantValidation(validationResult);
          })
          .catch((e) => {
            console.log("Merchant validation error:", e);
            doErrorMagic(e as Error, INTERNAL_ERROR_TYPE.APPLE_PAY.OnValidation, {
              event,
              validationResult,
              validationURL: event.validationURL,
            });
          });
        /* eslint-enable @typescript-eslint/no-unsafe-assignment */
      };

      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778009-onshippingcontactselected
      session.onshippingcontactselected = (event: ApplePayShippingContactSelectedEvent) => {
        // A little bit more complicated. It does a bit more initializing magic
        void asyncPutShippingCountry({
          shippingContact: event.shippingContact,
          cart,
          checkout: checkoutRef.current,
          session,
          shippingMethods: shippingMethodsRef.current,
        })
          .then((shippingMethods: ApplePayShippingMethod[] | undefined) => {
            shippingMethodsRef.current = shippingMethods;
          })
          .catch((e) => {
            console.log("shipping country selection error:", e);
            doErrorMagic(e as Error, INTERNAL_ERROR_TYPE.APPLE_PAY.OnShippingContactSelect, {event});
          });
      };

      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778028-onshippingmethodselected
      session.onshippingmethodselected = (event: ApplePayShippingMethodSelectedEvent) => {
        void asyncPutShippingMethod(event, checkoutRef.current, session).catch((e) => {
          console.log("shipping method selection error:", e);
          doErrorMagic(e as Error, INTERNAL_ERROR_TYPE.APPLE_PAY.OnShippingMethodSelect, {event});
        });
      };

      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778013-onpaymentmethodselected
      session.onpaymentmethodselected = (event: ApplePayPaymentMethodSelectedEvent) => {
        void putPaymentMethod(checkoutRef.current, session).catch((err) => {
          console.log("payment method selection error:", err);
          doErrorMagic(err as Error, INTERNAL_ERROR_TYPE.APPLE_PAY.OnPaymentSelect, {event});
        });
      };

      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/3813982-oncouponcodechanged
      session.oncouponcodechanged = (event: ApplePayCouponCodeChangedEvent) => {
        void asyncCouponCodeChange(event, cart, session).catch((e) => {
          doErrorMagic(e as Error, INTERNAL_ERROR_TYPE.APPLE_PAY.OnCouponSelect, {event});
        });
      };

      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778020-onpaymentauthorized
      // node_modules/braintree-web/apple-pay/apple-pay.js:305
      session.onpaymentauthorized = (event: ApplePayPaymentAuthorizedEvent) => {
        const {billingContact, shippingContact} = event.payment;

        const errorParamsArray: [ApplePayErrorCode, ApplePayErrorContactField | undefined, string][] = [];
        if (!paymentDataRef.current) {
          errorParamsArray.push(["unknown", undefined, "There's no `paymentDataRef.current` on payment authorization"]);
        }
        if (!shippingContact) {
          errorParamsArray.push(["shippingContactInvalid", undefined, "Shipping contact invalid"]);
        }
        if (shippingContact && !shippingContact.addressLines) {
          errorParamsArray.push(["shippingContactInvalid", "addressLines", "No shipping street and number given"]);
        }
        if (!billingContact) {
          errorParamsArray.push(["billingContactInvalid", undefined, "Billing contact invalid"]);
        }
        if (billingContact && !billingContact.addressLines) {
          errorParamsArray.push(["billingContactInvalid", "addressLines", "Billing address incomplete"]);
        }

        if (errorParamsArray.length) {
          session.completePayment({
            status: ApplePaySession.STATUS_FAILURE,
            errors: errorParamsArray.map((errorParams) => new ApplePayError(...errorParams)),
          });

          return;
        }

        if (shippingContact && billingContact && checkoutRef.current) {
          void checkoutRef.current
            .put({
              address: {
                delivery: appleAddressAdapter(shippingContact),
                invoice: {
                  ...appleAddressAdapter(billingContact),
                  email: shippingContact.emailAddress || billingContact.emailAddress || "",
                },
              },
            })
            .then(() => {
              if (!paymentDataRef.current) {
                return;
              }
              void asyncPaymentAuthorize(event, paymentDataRef.current.applePayInstance, session, doErrorMagic).then(
                (nonce) => {
                  if (nonce === "error") return;

                  setViewState({
                    ...viewState,
                    appleData: {nonce, paymentSource: "applepay"},
                    gPayData: undefined,
                    paypalInfos: undefined,
                  });

                  onReturn();
                },
              );
            });
        }
      };

      session.oncancel = (event) => {
        // eslint-disable-next-line no-console
        console.log("oncancel event:", event);
        void checkoutRef.current?.put({paymentId: null});
        session.abort();
      };

      // eslint-disable-next-line no-console
      console.log("begin session");
      session.begin();
    } catch (err) {
      doErrorMagic(err as Error, INTERNAL_ERROR_TYPE.APPLE_PAY.OnInit);
    }
  };

  if (elemRef.current && !elemRef.current.onclick) {
    // Has to be assigned on the native element, because of the
    // differing React behaviour related to `onClick` attributes
    elemRef.current.onclick = onApplePayButtonClicked;
  }

  return (
    <div className={`apple-pay-button-container ${isBlocked ? "blocked" : ""}`}>
      <apple-pay-button
        ref={elemRef}
        buttonstyle="white-outline"
        type="continue"
        locale={isoLocales[getShopId()].replace("_", "-")}
      ></apple-pay-button>
    </div>
  );
};

export default ApplePayButtonLoader;
